/**
 * EdgeDetailPanel Component (Issue #434)
 *
 * Displays HTTP request/response details when clicking on edges in E2E flow diagrams.
 * Shows the data format being transmitted between nodes (api-call → api-route → api-response).
 *
 * Issue #438: Enhanced with contextual evidence sections and database query details.
 * - Contextual labels for source/target evidence (Calling Code, Handler Code, etc.)
 * - Collapsible code sections for better organization
 * - Database query details (operation, table, fields, WHERE)
 */

import { useState } from 'react';
import type { EdgeInfo, CodeEvidence, TypedSchemaField, EdgeDatabaseInfo } from '@veodiagram/shared';

interface EdgeDetailPanelProps {
  edge: EdgeInfo | null;
  isOpen: boolean;
  onClose: () => void;
}

// Edge type labels and colors
const EDGE_TYPE_LABELS: Record<string, string> = {
  request: 'HTTP Request',
  response: 'HTTP Response',
  callback: 'Callback',
  database: 'Database Query',
  other: 'Connection',
};

const EDGE_TYPE_COLORS: Record<string, string> = {
  request: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  response: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  callback: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  database: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  other: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
};

// HTTP method colors
const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  POST: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  PUT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  PATCH: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  DELETE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

// Status code colors
function getStatusColor(statusCode: number): string {
  if (statusCode >= 200 && statusCode < 300) {
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  }
  if (statusCode >= 300 && statusCode < 400) {
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  }
  if (statusCode >= 400 && statusCode < 500) {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }
  if (statusCode >= 500) {
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  }
  return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400';
}

/**
 * Detail row component for displaying label-value pairs
 */
function DetailRow({ label, value }: { label: string; value: string | string[] | undefined }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  const displayValue = Array.isArray(value) ? value.join(', ') : value;
  return (
    <div className="flex gap-2 text-sm py-1">
      <span className="text-slate-500 dark:text-slate-400 flex-shrink-0 w-28">{label}:</span>
      <span className="text-slate-700 dark:text-slate-300 font-mono text-xs break-all">{displayValue}</span>
    </div>
  );
}

/**
 * Issue #435: Format a typed field for display
 */
function formatFieldType(field: TypedSchemaField): string {
  let type = field.type;
  if (field.arrayType) {
    type = `${field.arrayType}[]`;
  }
  if (field.enumValues && field.enumValues.length > 0) {
    type = field.enumValues.map(v => `"${v}"`).join(' | ');
  }
  if (field.isNullable) {
    type = `${type} | null`;
  }
  return type;
}

/**
 * Issue #435: Render a typed field with proper styling
 */
function TypedFieldDisplay({ field, isLast }: { field: TypedSchemaField; isLast: boolean }) {
  const typeDisplay = formatFieldType(field);
  const isOptional = field.isOptional;

  return (
    <div className="py-0.5">
      <span className="text-blue-400">&quot;{field.name}&quot;</span>
      {isOptional && <span className="text-slate-500">?</span>}
      <span className="text-slate-500">: </span>
      <span className={field.type === 'unknown' || field.type === 'any' ? 'text-amber-400 italic' : 'text-green-400'}>
        {typeDisplay}
      </span>
      {!isLast && <span className="text-slate-500">,</span>}
    </div>
  );
}

/**
 * Fields list component with JSON-like display
 * Issue #435: Now supports typed fields with actual type information
 */
function FieldsList({
  fields,
  typedFields,
  title,
  schemaName
}: {
  fields?: string[];
  typedFields?: TypedSchemaField[];
  title: string;
  schemaName?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Use typed fields if available, otherwise fall back to string fields
  const hasTypedFields = typedFields && typedFields.length > 0;
  const hasStringFields = fields && fields.length > 0;

  if (!hasTypedFields && !hasStringFields) return null;

  const fieldCount = hasTypedFields ? typedFields!.length : fields!.length;

  return (
    <div className="mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
      >
        <svg
          className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span>{title}</span>
        {schemaName && (
          <code className="px-1.5 py-0.5 text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
            {schemaName}
          </code>
        )}
        <span className="text-slate-400">({fieldCount} fields)</span>
        {hasTypedFields && (
          <span className="px-1.5 py-0.5 text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
            typed
          </span>
        )}
      </button>
      {isExpanded && (
        <div className="mt-2 ml-4 p-3 bg-slate-900 dark:bg-slate-950 rounded-md font-mono text-xs overflow-x-auto">
          <div className="text-slate-400">{'{'}</div>
          <div className="ml-3">
            {hasTypedFields ? (
              // Render typed fields with actual types
              typedFields!.map((field, i) => (
                <TypedFieldDisplay
                  key={field.name}
                  field={field}
                  isLast={i === typedFields!.length - 1}
                />
              ))
            ) : (
              // Fallback to string fields with 'any' type
              fields!.map((field, i) => (
                <div key={field} className="py-0.5">
                  <span className="text-blue-400">&quot;{field}&quot;</span>
                  <span className="text-slate-500">: </span>
                  <span className="text-amber-400 italic">any</span>
                  {i < fields!.length - 1 && <span className="text-slate-500">,</span>}
                </div>
              ))
            )}
          </div>
          <div className="text-slate-400">{'}'}</div>
        </div>
      )}
    </div>
  );
}

/**
 * Evidence card component
 */
function EvidenceCard({ evidence, index }: { evidence: CodeEvidence; index: number }) {
  const [isExpanded, setIsExpanded] = useState(index === 0);
  const fileName = evidence.filePath.split('/').pop() || evidence.filePath;

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 flex items-center justify-between gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm min-w-0">
          <span className="text-slate-400 flex-shrink-0">
            {isExpanded ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </span>
          <span className="font-mono text-xs text-slate-600 dark:text-slate-300 truncate">
            {fileName}
          </span>
          <span className="text-xs text-slate-400 flex-shrink-0">
            L{evidence.lineStart}
            {evidence.lineEnd !== evidence.lineStart && `-${evidence.lineEnd}`}
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700">
          {evidence.snippet ? (
            <pre className="p-3 text-xs overflow-x-auto bg-slate-900 dark:bg-slate-950">
              <code className="text-slate-100">
                {evidence.snippet.split('\n').map((line, i) => (
                  <div key={i} className="flex">
                    <span className="w-8 text-slate-500 select-none text-right pr-3">
                      {evidence.lineStart + i}
                    </span>
                    <span className="flex-1">{line || ' '}</span>
                  </div>
                ))}
              </code>
            </pre>
          ) : (
            <div className="px-3 py-4 bg-slate-50 dark:bg-slate-800/50 text-center">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                Code snippet not available
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 font-mono">
                {evidence.filePath}:{evidence.lineStart}-{evidence.lineEnd}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Issue #438: Collapsible code section for contextual evidence display
 * Shows evidence with a contextual label (e.g., "Calling Code", "Handler Code")
 */
function CollapsibleCodeSection({
  title,
  evidence,
  defaultExpanded = true,
  icon,
  iconColor = 'text-blue-500',
}: {
  title: string;
  evidence: CodeEvidence[];
  defaultExpanded?: boolean;
  icon?: React.ReactNode;
  iconColor?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (!evidence || evidence.length === 0) return null;

  return (
    <div className="border-b border-slate-200 dark:border-slate-700">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        <span className="text-slate-400 flex-shrink-0">
          {isExpanded ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </span>
        {icon && <span className={iconColor}>{icon}</span>}
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          {title}
        </span>
        <span className="text-xs text-slate-400 ml-auto">
          {evidence.length} {evidence.length === 1 ? 'file' : 'files'}
        </span>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-2">
          {evidence.map((ev, index) => (
            <EvidenceCard key={index} evidence={ev} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Issue #438: Database query details component
 * Shows operation, table, model, fields, and WHERE conditions
 */
function DatabaseQueryDetails({ database }: { database: EdgeDatabaseInfo }) {
  if (!database) return null;

  const hasDetails = database.operation || database.table || database.model ||
    (database.fields && database.fields.length > 0) ||
    (database.whereFields && database.whereFields.length > 0);

  if (!hasDetails) return null;

  // Operation colors
  const operationColors: Record<string, string> = {
    select: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    insert: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    update: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    delete: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    upsert: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    findMany: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    findUnique: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    findFirst: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    create: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    createMany: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    updateMany: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    deleteMany: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  const operationColor = database.operation
    ? operationColors[database.operation.toLowerCase()] || 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
    : '';

  return (
    <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700">
      <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-3">
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
        Query Details
      </h4>

      {/* Operation and Table */}
      <div className="flex items-center gap-2 mb-3">
        {database.operation && (
          <span className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${operationColor}`}>
            {database.operation}
          </span>
        )}
        {database.table && (
          <code className="text-sm font-mono text-slate-700 dark:text-slate-300">
            {database.model ? `${database.model} (${database.table})` : database.table}
          </code>
        )}
      </div>

      {/* Fields */}
      {database.fields && database.fields.length > 0 && (
        <div className="mb-3">
          <span className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
            {database.operation?.toLowerCase().includes('select') || database.operation?.toLowerCase().includes('find')
              ? 'Selected Fields'
              : 'Modified Fields'}
          </span>
          <div className="flex flex-wrap gap-1">
            {database.fields.map((field, i) => (
              <code
                key={i}
                className="px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded"
              >
                {field}
              </code>
            ))}
          </div>
        </div>
      )}

      {/* WHERE Fields */}
      {database.whereFields && database.whereFields.length > 0 && (
        <div>
          <span className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
            WHERE Conditions
          </span>
          <div className="flex flex-wrap gap-1">
            {database.whereFields.map((field, i) => (
              <code
                key={i}
                className="px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded"
              >
                {field}
              </code>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * EdgeDetailPanel - Main panel component
 */
export function EdgeDetailPanel({ edge, isOpen, onClose }: EdgeDetailPanelProps) {
  if (!isOpen || !edge) return null;

  const edgeTypeLabel = EDGE_TYPE_LABELS[edge.type] || 'Connection';
  const edgeTypeColor = EDGE_TYPE_COLORS[edge.type] || EDGE_TYPE_COLORS.other;
  const hasEvidence = edge.evidence && edge.evidence.length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-[480px] max-w-full bg-white dark:bg-slate-900 shadow-xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            {edgeTypeLabel}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Connection Info */}
        <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-center gap-3">
            <div className="text-center flex-1">
              <span className="block text-xs text-slate-400 mb-1">Source</span>
              <span className="inline-block px-2 py-1 text-sm font-medium bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 truncate max-w-full">
                {edge.sourceNode.label}
              </span>
            </div>
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
            <div className="text-center flex-1">
              <span className="block text-xs text-slate-400 mb-1">Target</span>
              <span className="inline-block px-2 py-1 text-sm font-medium bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 truncate max-w-full">
                {edge.targetNode.label}
              </span>
            </div>
          </div>
          <div className="flex justify-center mt-2">
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${edgeTypeColor}`}>
              {edgeTypeLabel}
            </span>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Issue #438: Contextual sections based on edge type */}

          {/* For "request" edges: Calling Code → Request Payload → Handler Code */}
          {edge.type === 'request' && (
            <>
              {/* Calling Code (source evidence) */}
              {edge.sourceEvidence && (
                <CollapsibleCodeSection
                  title={edge.sourceEvidence.label}
                  evidence={edge.sourceEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                    </svg>
                  }
                  iconColor="text-amber-500"
                />
              )}

              {/* Request Payload */}
              {edge.request && (
                <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Request Payload
                  </h4>

                  {/* Method and Endpoint */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${METHOD_COLORS[edge.request.method] || METHOD_COLORS.GET}`}>
                      {edge.request.method}
                    </span>
                    <code className="flex-1 text-sm font-mono text-slate-700 dark:text-slate-300 break-all">
                      {edge.request.endpoint}
                    </code>
                  </div>

                  {/* Request Details */}
                  <div className="space-y-1 border-t border-slate-100 dark:border-slate-800 pt-2">
                    <DetailRow label="Path Params" value={edge.request.pathParams} />
                    <DetailRow label="Query Params" value={edge.request.queryParams} />
                    <DetailRow label="Content Type" value={edge.request.contentType} />
                    <DetailRow label="Schema" value={edge.request.bodySchemaName} />
                  </div>

                  {/* Request Body Fields */}
                  {((edge.request.bodyFields && edge.request.bodyFields.length > 0) ||
                    (edge.request.bodyTypedFields && edge.request.bodyTypedFields.length > 0)) && (
                    <FieldsList
                      fields={edge.request.bodyFields}
                      typedFields={edge.request.bodyTypedFields}
                      title="Request Body"
                      schemaName={edge.request.bodySchemaName}
                    />
                  )}
                </div>
              )}

              {/* Handler Code (target evidence) */}
              {edge.targetEvidence && (
                <CollapsibleCodeSection
                  title={edge.targetEvidence.label}
                  evidence={edge.targetEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  }
                  iconColor="text-blue-500"
                />
              )}
            </>
          )}

          {/* For "response" edges: Handler Code → Response Payload → Callback Code */}
          {edge.type === 'response' && (
            <>
              {/* Handler Code (source evidence) */}
              {edge.sourceEvidence && (
                <CollapsibleCodeSection
                  title={edge.sourceEvidence.label}
                  evidence={edge.sourceEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  }
                  iconColor="text-blue-500"
                />
              )}

              {/* Response Payload */}
              {edge.response && (
                <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Response Payload
                  </h4>

                  {/* Status Code */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${getStatusColor(edge.response.statusCode)}`}>
                      {edge.response.statusCode}
                    </span>
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {edge.response.type}
                    </span>
                  </div>

                  {/* Response Details */}
                  <div className="space-y-1 border-t border-slate-100 dark:border-slate-800 pt-2">
                    <DetailRow label="Content Type" value={edge.response.contentType} />
                    <DetailRow label="Schema" value={edge.response.bodySchemaName} />
                  </div>

                  {/* Response Body Fields */}
                  {((edge.response.bodyFields && edge.response.bodyFields.length > 0) ||
                    (edge.response.bodyTypedFields && edge.response.bodyTypedFields.length > 0)) && (
                    <FieldsList
                      fields={edge.response.bodyFields}
                      typedFields={edge.response.bodyTypedFields}
                      title="Response Body"
                      schemaName={edge.response.bodySchemaName}
                    />
                  )}
                </div>
              )}

              {/* Callback Code (target evidence) */}
              {edge.targetEvidence && (
                <CollapsibleCodeSection
                  title={edge.targetEvidence.label}
                  evidence={edge.targetEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  }
                  iconColor="text-emerald-500"
                />
              )}
            </>
          )}

          {/* For "callback" edges: Response Code → Callback Code */}
          {edge.type === 'callback' && (
            <>
              {/* Response Code (source evidence) */}
              {edge.sourceEvidence && (
                <CollapsibleCodeSection
                  title={edge.sourceEvidence.label}
                  evidence={edge.sourceEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  }
                  iconColor="text-emerald-500"
                />
              )}

              {/* Callback Code (target evidence) */}
              {edge.targetEvidence && (
                <CollapsibleCodeSection
                  title={edge.targetEvidence.label}
                  evidence={edge.targetEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  }
                  iconColor="text-blue-500"
                />
              )}
            </>
          )}

          {/* For "database" edges: Query Code / Query Details / Result Handling Code */}
          {edge.type === 'database' && (
            <>
              {/* Query Code (source evidence - for query direction) */}
              {edge.sourceEvidence && (
                <CollapsibleCodeSection
                  title={edge.sourceEvidence.label}
                  evidence={edge.sourceEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  }
                  iconColor="text-pink-500"
                />
              )}

              {/* Query Details (always shown for database edges) */}
              {edge.database && <DatabaseQueryDetails database={edge.database} />}

              {/* Result Handling Code (target evidence - for result direction) */}
              {edge.targetEvidence && (
                <CollapsibleCodeSection
                  title={edge.targetEvidence.label}
                  evidence={edge.targetEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  }
                  iconColor="text-blue-500"
                />
              )}
            </>
          )}

          {/* For "other" edges: Show original request/response/evidence format */}
          {edge.type === 'other' && (
            <>
              {/* Request Section */}
              {edge.request && (
                <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                    </svg>
                    Request
                  </h4>

                  {/* Method and Endpoint */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${METHOD_COLORS[edge.request.method] || METHOD_COLORS.GET}`}>
                      {edge.request.method}
                    </span>
                    <code className="flex-1 text-sm font-mono text-slate-700 dark:text-slate-300 break-all">
                      {edge.request.endpoint}
                    </code>
                  </div>

                  {/* Request Details */}
                  <div className="space-y-1 border-t border-slate-100 dark:border-slate-800 pt-2">
                    <DetailRow label="Path Params" value={edge.request.pathParams} />
                    <DetailRow label="Query Params" value={edge.request.queryParams} />
                    <DetailRow label="Content Type" value={edge.request.contentType} />
                    <DetailRow label="Schema" value={edge.request.bodySchemaName} />
                  </div>

                  {/* Request Body Fields */}
                  {((edge.request.bodyFields && edge.request.bodyFields.length > 0) ||
                    (edge.request.bodyTypedFields && edge.request.bodyTypedFields.length > 0)) && (
                    <FieldsList
                      fields={edge.request.bodyFields}
                      typedFields={edge.request.bodyTypedFields}
                      title="Request Body"
                      schemaName={edge.request.bodySchemaName}
                    />
                  )}
                </div>
              )}

              {/* Response Section */}
              {edge.response && (
                <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    Response
                  </h4>

                  {/* Status Code */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${getStatusColor(edge.response.statusCode)}`}>
                      {edge.response.statusCode}
                    </span>
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {edge.response.type}
                    </span>
                  </div>

                  {/* Response Details */}
                  <div className="space-y-1 border-t border-slate-100 dark:border-slate-800 pt-2">
                    <DetailRow label="Content Type" value={edge.response.contentType} />
                    <DetailRow label="Schema" value={edge.response.bodySchemaName} />
                  </div>

                  {/* Response Body Fields */}
                  {((edge.response.bodyFields && edge.response.bodyFields.length > 0) ||
                    (edge.response.bodyTypedFields && edge.response.bodyTypedFields.length > 0)) && (
                    <FieldsList
                      fields={edge.response.bodyFields}
                      typedFields={edge.response.bodyTypedFields}
                      title="Response Body"
                      schemaName={edge.response.bodySchemaName}
                    />
                  )}
                </div>
              )}

              {/* Source Evidence */}
              {edge.sourceEvidence && (
                <CollapsibleCodeSection
                  title={edge.sourceEvidence.label}
                  evidence={edge.sourceEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  }
                  iconColor="text-blue-500"
                />
              )}

              {/* Target Evidence */}
              {edge.targetEvidence && (
                <CollapsibleCodeSection
                  title={edge.targetEvidence.label}
                  evidence={edge.targetEvidence.evidence}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  }
                  iconColor="text-blue-500"
                />
              )}
            </>
          )}

          {/* Empty State */}
          {!edge.request && !edge.response && !edge.database && !hasEvidence && !edge.sourceEvidence && !edge.targetEvidence && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-base font-medium text-slate-600 dark:text-slate-400 mb-1">
                No Details Available
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-500 max-w-xs">
                This edge doesn&apos;t have HTTP request/response details. It may be a non-HTTP connection.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default EdgeDetailPanel;
